<template>
  <Modal
    v-if="modals.reminder && availableReminders.length && players[playerIndex]"
    class="reminder-picker"
    @close="toggleModal('reminder')"
  >
    <!-- WRITE THE NOTE HERE, never prompt(). A browser dialog is silently
         auto-dismissed in dialog-less contexts (driven panes, embeds, some
         webviews) and comes back empty — so the old `if (!name) return`
         swallowed every custom note and the token simply never appeared.
         The same trap took the Leave door (FT-852) and the script editor's
         save (which is why EditionModal's fork panel exists); this is the
         same answer in the same shape.

         It REPLACES the token list rather than sitting under it: the choice
         has already been made by the time this shows, and Cancel puts the
         list back with nothing added. -->
    <div class="custom-note" v-if="note !== null">
      <h3>Add a custom note</h3>
      <label>Note</label>
      <input
        ref="noteInput"
        class="cn-name"
        v-model="note"
        maxlength="60"
        @keyup.enter="commitNote"
        @keyup.esc="cancelNote"
      />
      <div class="cn-error" v-if="noteError">{{ noteError }}</div>
      <div class="cn-acts">
        <div class="button" @click="cancelNote">
          <font-awesome-icon icon="times" /> Cancel
        </div>
        <div class="button cn-go" @click="commitNote">
          <font-awesome-icon icon="check" /> Add note
        </div>
      </div>
    </div>
    <template v-else>
      <h3>Choose a reminder token:</h3>
      <ul class="reminders">
        <li
          v-for="reminder in availableReminders"
          class="reminder"
          :class="[reminder.role]"
          :key="reminder.role + ' ' + reminder.name"
          @click="addReminder(reminder)"
        >
          <span
            class="icon"
            :style="{
              backgroundImage: `url(${
                reminder.image && grimoire.isImageOptIn
                  ? reminder.image
                  : require('../../assets/icons/' +
                      (reminder.imageAlt || reminder.role) +
                      '.png')
              })`
            }"
          ></span>
          <span class="text">{{ reminder.name }}</span>
        </li>
      </ul>
      <!-- FT-1393: THE SCRIPT'S ROLE ICONS, grouped and labeled by type, so
           the storyteller can drop ANY role's token on a chair by hand — a
           minion marker on the demon, a fake demon on the Lunatic, whatever
           the night calls for. These are the SAME tokens the deal's
           evil-info seeding places (golem/evilInfo.js's roleToken), so a
           hand-placed one and a dealt one are the same object on the seat. -->
      <div
        class="role-group"
        v-for="group in roleTokenGroups"
        :key="group.team"
      >
        <h4 class="rg-label">{{ group.label }}</h4>
        <ul class="reminders">
          <li
            v-for="token in group.tokens"
            class="reminder"
            :class="[token.role]"
            :key="'role ' + token.role"
            @click="addReminder(token)"
          >
            <span
              class="icon"
              :style="{
                backgroundImage: `url(${
                  token.image && grimoire.isImageOptIn
                    ? token.image
                    : require(
                        '../../assets/icons/' +
                          (token.imageAlt || token.role) +
                          '.png',
                      )
                })`,
              }"
            ></span>
            <span class="text">{{ token.name }}</span>
          </li>
        </ul>
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import Modal from "./Modal.vue";
// FT-1117: a reminder entry may be a plain string or an authored object.
import { reminderName } from "../../golem/dealReminders";
// FT-1393: the role-icon groups below place the same token object the deal's
// evil-info seeding builds — one builder, so the two can never drift.
import { roleToken } from "../../golem/evilInfo";
/**
 * One reminder entry of a role, as the token object a seat keeps in its
 * `reminders` list: `{ role, image, imageAlt, name }`.
 *
 * FT-1117: an authored reminder is a plain string OR `{ name, deal }` — a
 * character may declare that the deal places the token itself (the Fortune
 * Teller's red herring). `reminderName` is the one reader for both shapes, so
 * an entry carrying a rule still offers the same tile in this picker; only
 * where it CAME from differs. The object this builds is also, deliberately,
 * the object golem/dealReminders.js builds — a dealt token and a hand-placed
 * one are the same thing on the seat.
 */
const mapReminder = ({ id, image, imageAlt }) => entry => ({
  role: id,
  image,
  imageAlt,
  name: reminderName(entry)
});

// Always offered, after everything the town brings. Their ids pick the art
// (assets/icons/<id>.png) and `custom` opens the note panel.
const FIXED_TILES = [
  { role: "good", name: "Good" },
  { role: "evil", name: "Evil" },
  { role: "custom", name: "Write a note" },
];

// FT-1475 (Clocktower's, ported FT-1538): the Lot editor's "Ability spent"
// option — a Lot saved with it on (golemSpentToken, from its
// night.spentToken) offers this token too, beside its own. A marker only:
// nothing enforces a once-per-game ability.
const SPENT_TOKEN = "Ability spent";

// every reminder entry a role lists under `key`, as tokens — plus, for its
// own list, the Ability spent token where the Lot asks for one (unless it
// already lists a token of that name)
const tokensOf = (role, key = "reminders") => {
  const own = (role[key] || []).map(mapReminder(role));
  if (
    key === "reminders" &&
    role.golemSpentToken &&
    !own.some((t) => t.name === SPENT_TOKEN)
  ) {
    own.push(mapReminder(role)(SPENT_TOKEN));
  }
  return own;
};

export default {
  components: { Modal },
  props: {
    // the seat the picked token lands on
    playerIndex: { type: Number, required: true },
  },
  data() {
    return {
      // null = the token list is showing; a string = the note field is open.
      // "" is a legal open state, which is why this is null-vs-string and not
      // a truthiness test.
      note: null,
      noteError: "",
    };
  },
  watch: {
    // The modal can be closed from outside this panel (the shell's ×, the
    // backdrop, a hotkey). Whatever was half-typed goes with it, so the next
    // open starts on the token list rather than on a stale field.
    "modals.reminder"(open) {
      if (!open) this.closeNote();
    },
  },
  computed: {
    /**
     * Every tile the picker offers, duplicates kept: the script's roles (own
     * reminders only when seated or bluffed; global ones always), then the
     * fabled, then any off-script traveller someone is seated as, then the
     * three fixed tiles.
     */
    availableReminders() {
      const { players, bluffs, fabled } = this.$store.state.players;
      const seated = new Set(players.map((player) => player.role.id));
      const bluffed = new Set(bluffs.map((bluff) => bluff && bluff.id));
      const fromScript = [...this.$store.state.roles.values()].flatMap(
        (role) => [
          ...(seated.has(role.id) || bluffed.has(role.id)
            ? tokensOf(role)
            : []),
          ...tokensOf(role, "remindersGlobal"),
        ],
      );
      const fromTravelers = [...this.$store.state.otherTravelers.values()]
        .filter((traveler) => seated.has(traveler.id))
        .flatMap((traveler) => tokensOf(traveler));
      return [
        ...fromScript,
        ...fabled.flatMap((entry) => tokensOf(entry)),
        ...fromTravelers,
        ...FIXED_TILES,
      ];
    },
    /**
     * FT-1393: every character on the current script as a placeable role
     * token, grouped and labeled by type. Travellers are left out — they
     * are added mid-game through their own flow, never marked onto a chair.
     * Empty groups are filtered HERE so the template never pairs v-if with
     * v-for.
     */
    roleTokenGroups() {
      const groups = [
        { team: "townsfolk", label: "Townsfolk", tokens: [] },
        { team: "outsider", label: "Outsider", tokens: [] },
        { team: "minion", label: "Minion", tokens: [] },
        { team: "demon", label: "Demon", tokens: [] },
      ];
      this.$store.state.roles.forEach((role) => {
        if (!role || !role.id) return;
        const group = groups.find((g) => g.team === role.team);
        if (group) group.tokens.push(roleToken(role));
      });
      return groups.filter((group) => group.tokens.length);
    },
    ...mapState(["modals", "grimoire"]),
    ...mapState("players", ["players"]),
  },
  methods: {
    addReminder(reminder) {
      // The custom token doesn't add anything yet — it asks first.
      if (reminder.role === "custom") return this.openNote();
      this.commitReminder(reminder);
    },
    openNote() {
      this.note = "";
      this.noteError = "";
      this.$nextTick(() => {
        const el = this.$refs.noteInput;
        if (el) el.focus();
      });
    },
    /** Cancel adds NOTHING and puts the token list back. */
    cancelNote() {
      this.closeNote();
    },
    closeNote() {
      this.note = null;
      this.noteError = "";
    },
    commitNote() {
      const name = (this.note || "").trim();
      // An empty note would land a blank token nobody can read. Say so where
      // the typing is happening rather than closing on a silent no-op — the
      // silent no-op is the bug this panel replaces.
      if (!name) {
        this.noteError = "Type the note first, or cancel.";
        return;
      }
      this.closeNote();
      this.commitReminder({ role: "custom", name });
    },
    commitReminder(reminder) {
      const player = this.$store.state.players.players[this.playerIndex];
      this.$store.commit("players/update", {
        player,
        property: "reminders",
        value: [...player.reminders, reminder]
      });
      this.toggleModal("reminder");
    },
    ...mapMutations(["toggleModal"]),
  },
};
</script>

<style scoped lang="scss">
// The note field, wearing the workbench's fork-panel shape (its own comment
// explains why that panel is a panel and not a prompt).
.custom-note {
  width: min(420px, 92%);
  text-align: left;

  h3 {
    margin: 0 0 8px;
    font-size: 22px;
  }

  label {
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    opacity: 0.6;
    margin-bottom: 4px;
  }

  .cn-name {
    width: 100%;
    font-size: 17px;
    padding: 7px 12px;
    margin: 0;
    color: white;
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid #3d3d3d;
    border-radius: 5px;
    &:focus {
      outline: none;
      border-color: #a01414;
    }
  }

  .cn-error {
    margin: 8px 0 0;
    font-size: 13px;
    color: #ff7070;
  }

  .cn-acts {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 14px;

    // Our buttons, not upstream's shiny pills: small, flat, dark, hairline —
    // the same treatment the workbench's own panels use.
    .button {
      margin: 0;
      padding: 2px 9px;
      border: 1px solid #3d3d3d;
      border-radius: 5px;
      background: rgba(0, 0, 0, 0.65);
      box-shadow: none;
      font-weight: normal;
      font-size: 13px;
      line-height: 1.6;
      cursor: pointer;
      &:before,
      &:after {
        content: none;
      }
      &:hover {
        border-color: #a01414;
        color: #ff7070;
      }
    }

    .cn-go:hover {
      background: rgba(160, 20, 20, 0.35);
      color: white;
    }
  }
}

ul.reminders .reminder {
  // (user call 2026-08-20: "those need to use our player coins as the
  // background") — the SAME ground the seats' own reminders took in FT-940,
  // and the same one the character coins wear: `--coin`, painted onto the
  // document root by golem/coinArt.js's applyCoin(). Reading the property
  // rather than naming a file is what makes these tiles repaint with the
  // rest of the app when a different coin is picked.
  //
  // This rule lives HERE and not with the seats' because the picker's tiles
  // render inside the modal, outside `.circle` — which is also why FT-940's
  // fix reached the seats and left these behind.
  background: var(--coin, url("../../assets/token-golem.png")) center center;
  background-size: 100%;
  // a seat's reminder token, at picker size
  width: min(14vh, 100px);
  height: min(14vh, 100px);
  margin: 1%;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 3px solid #000;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.6);
  line-height: 1;
  cursor: pointer;
  transition: transform 450ms;

  .icon {
    position: absolute;
    top: 0;
    width: 90%;
    height: 90%;
    background: center / contain no-repeat;
  }

  .text {
    top: 24%;
    width: 80%;
    color: #000;
    font-size: 66%;
    font-weight: bold;
    line-height: 1;
    text-align: center;
    // (user call 2026-08-20: "the text on the reminders needs the white glow
    // for readability") — the SAME four-way pale halo the seats' own reminders
    // wear (Player.vue's `.circle .reminder .text`). Black type on the coin's
    // parchment was legible on the old flat ground and stopped being so the
    // moment these tiles took the real coin art, which is textured and darkens
    // toward its rim. Four offsets rather than a blur: a blur spreads and
    // greys at this size, where the offsets keep the letterform's own edge.
    text-shadow:
      0 1px 1px #f6dfbd,
      0 -1px 1px #f6dfbd,
      1px 0 1px #f6dfbd,
      -1px 0 1px #f6dfbd;
  }

  &:hover {
    transform: scale(1.2);
  }
}

// FT-1393: a group's label — the same small uppercase treatment the note
// panel's field label wears, sitting flush left above its row of tokens.
.role-group {
  width: 100%;

  .rg-label {
    margin: 12px 0 2px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 2px;
    opacity: 0.6;
    text-align: left;
  }
}
</style>

<style lang="scss">
// FT-1393: UNSCOPED, deliberately — the scroller is Modal.vue's own `.slot`,
// which a scoped rule cannot reach. The class rides the backdrop (component
// class passthrough, the workbench's idiom). With the script's full role
// roster now in the picker the list outgrows the dialog on every real
// script, and the slot is the element that has to scroll.
.modal-backdrop.reminder-picker .modal > .slot {
  overflow-y: auto;
  overscroll-behavior: contain;
}
</style>
